import { create } from 'zustand';

interface BatchSnapshot {
  status: string;
  processed_count: number;
  succeeded_count: number;
  failed_count: number;
  completed_at?: string | null;
}

export interface QueuedBatchTransfer {
  revision: number;
  baseline?: BatchSnapshot;
}

interface TrackedBatchTransfer {
  queued?: QueuedBatchTransfer;
}

interface BatchTransferActivityStore {
  byUser: Record<string, Record<string, TrackedBatchTransfer>>;
  track: (userId: string, ids: string[]) => void;
  queue: (userId: string, id: string, baseline?: BatchSnapshot) => void;
  observe: (
    userId: string,
    id: string,
    snapshot: BatchSnapshot,
    requestRevision: number | undefined
  ) => void;
  finish: (userId: string, id: string) => void;
}

const terminalStatuses = new Set([
  'completed',
  'partially_completed',
  'failed',
  'cancelled',
  'validation_failed',
]);
let acceptanceRevision = 0;

function hasTerminalAdvance(snapshot: BatchSnapshot, baseline?: BatchSnapshot) {
  if (!terminalStatuses.has(snapshot.status)) return false;
  if (!baseline) return Boolean(snapshot.completed_at);
  if (snapshot.status !== baseline.status) return true;
  if (snapshot.completed_at && snapshot.completed_at !== baseline.completed_at) {
    const completedAt = Date.parse(snapshot.completed_at);
    const baselineAt = baseline.completed_at ? Date.parse(baseline.completed_at) : 0;
    if (Number.isFinite(completedAt) && completedAt > baselineAt) return true;
  }
  return (
    snapshot.processed_count !== baseline.processed_count ||
    snapshot.succeeded_count !== baseline.succeeded_count ||
    snapshot.failed_count !== baseline.failed_count
  );
}

/** Session-only tracking, scoped by user. It never persists payment or file data. */
export const useBatchTransferActivityStore = create<BatchTransferActivityStore>((set, get) => ({
  byUser: {},
  track: (userId, ids) =>
    set(state => {
      const tracked = state.byUser[userId] ?? {};
      const next = { ...tracked };
      ids.forEach(id => {
        next[id] ??= {};
      });
      return { byUser: { ...state.byUser, [userId]: next } };
    }),
  queue: (userId, id, baseline) =>
    set(state => ({
      byUser: {
        ...state.byUser,
        [userId]: {
          ...state.byUser[userId],
          [id]: {
            queued: {
              revision: ++acceptanceRevision,
              baseline: baseline
                ? {
                    status: baseline.status,
                    processed_count: baseline.processed_count,
                    succeeded_count: baseline.succeeded_count,
                    failed_count: baseline.failed_count,
                    completed_at: baseline.completed_at,
                  }
                : undefined,
            },
          },
        },
      },
    })),
  observe: (userId, id, snapshot, requestRevision) => {
    const queued = get().byUser[userId]?.[id]?.queued;
    // A GET begun before acceptance (or before a later retry) cannot settle this request.
    if (!queued || requestRevision !== queued.revision) return;
    if (snapshot.status !== 'processing' && !hasTerminalAdvance(snapshot, queued.baseline)) return;
    set(state => ({
      byUser: {
        ...state.byUser,
        [userId]: { ...state.byUser[userId], [id]: {} },
      },
    }));
  },
  finish: (userId, id) =>
    set(state => {
      const tracked = { ...state.byUser[userId] };
      delete tracked[id];
      return { byUser: { ...state.byUser, [userId]: tracked } };
    }),
}));
